import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { melhorEnvioApi } from "@/services";
import { useTenant } from "./useTenant";
import { useAllIntegrationsStatus } from "./useIntegrationsStatus";
import { toast } from "sonner";

interface MelhorEnvioUser {
  email?: string;
  firstname?: string;
  lastname?: string;
  document?: string;
}

interface IntegrationStatus {
  connected: boolean;
  user?: MelhorEnvioUser;
  expires_at?: string;
}

interface ShippingService {
  id: number;
  name: string;
  price: string;
  custom_price: string;
  discount: string;
  currency: string;
  delivery_time: number;
  delivery_range: {
    min: number;
    max: number;
  };
  company: {
    id: number;
    name: string;
    picture: string;
  };
}

interface CalculateShippingParams {
  from: {
    postal_code: string;
  };
  to: {
    postal_code: string;
  };
  products: Array<{
    id?: string;
    width: number;
    height: number;
    length: number;
    weight: number;
    insurance_value: number;
    quantity: number;
  }>;
}

interface CreateShipmentParams {
  sale_id: string;
  service: {
    id: number;
    name: string;
    price: number;
  };
  from: {
    name: string;
    phone: string;
    email: string;
    document: string;
    company_document?: string;
    state_register?: string;
    address: string;
    complement?: string;
    number: string;
    district: string;
    city: string;
    country_id: string;
    postal_code: string;
    note?: string;
  };
  to: {
    name: string;
    phone: string;
    email: string;
    document: string;
    company_document?: string;
    state_register?: string;
    address: string;
    complement?: string;
    number: string;
    district: string;
    city: string;
    state_abbr: string;
    country_id: string;
    postal_code: string;
    note?: string;
  };
  products: Array<{
    name: string;
    quantity: number;
    unitary_value: number;
  }>;
  volumes?: Array<{
    height: number;
    width: number;
    length: number;
    weight: number;
  }>;
}

export function useIntegrationStatus() {
  const { data: integrations, isLoading } = useAllIntegrationsStatus();

  const data = useMemo((): IntegrationStatus => {
    const integration = integrations?.find(
      (i) => i.provider === "melhor_envio" && i.is_active
    );
    if (!integration) return { connected: false };
    const metadata = integration.metadata as Record<string, unknown> | null;
    return {
      connected: true,
      user: (metadata as MelhorEnvioUser) || undefined,
      expires_at: integration.expires_at || undefined,
    };
  }, [integrations]);

  return { data, isLoading };
}

export function useConnectMelhorEnvio() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async () => {
      const redirectUri = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/melhor-envio-auth/callback`;

      const data = await melhorEnvioApi.startAuth(redirectUri);

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        data.auth_url,
        "melhor-envio-oauth",
        `width=${width},height=${height},left=${left},top=${top}`
      );

      return new Promise<void>((resolve, reject) => {
        const cleanup = () => {
          clearInterval(checkClosed);
          clearTimeout(timeout);
          window.removeEventListener("message", handleMessage);
        };

        const handleMessage = (event: MessageEvent) => {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
          if (supabaseUrl && event.origin !== new URL(supabaseUrl).origin) return;
          if (event.data?.type === "melhor-envio-success") {
            cleanup();
            queryClient.invalidateQueries({ queryKey: ["integrations-status", tenant?.id] });
            resolve();
          } else if (event.data?.type === "melhor-envio-error") {
            cleanup();
            reject(new Error(event.data.error || "OAuth failed"));
          }
        };

        window.addEventListener("message", handleMessage);

        const checkClosed = setInterval(() => {
          if (popup?.closed) {
            cleanup();
            reject(new Error("Autenticação cancelada"));
          }
        }, 1000);

        const timeout = setTimeout(() => {
          cleanup();
          popup?.close();
          reject(new Error("Tempo limite da autenticação"));
        }, 5 * 60 * 1000);
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao conectar Melhor Envio");
    },
  });
}

export function useDisconnectMelhorEnvio() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async () => {
      await melhorEnvioApi.disconnect();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations-status", tenant?.id] });
      toast.success("Melhor Envio desconectado");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Erro ao desconectar Melhor Envio");
    },
  });
}

export function useCalculateShipping() {
  return useMutation({
    mutationFn: async (params: CalculateShippingParams): Promise<ShippingService[]> => {
      const data = await melhorEnvioApi.calculateShipping(params);

      const validServices = (data as ShippingService[]).filter(
        (service) => !("error" in service)
      );

      return validServices;
    },
  });
}

export function useCreateShipment() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async (params: CreateShipmentParams) => {
      return await melhorEnvioApi.createShipment(params);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["shipments", variables.sale_id, tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["sales", tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-metrics", tenant?.id] });
    },
  });
}
